/**
 * Task 151-D.1 — pure render-decision for the package progress section.
 *
 * Kept React-free (no React / no component imports) so it is unit-testable
 * in the Node vitest environment, mirroring
 * `supabase/functions/get-client-package-progress/entitlement.ts`.
 *
 * The section must stay VISIBLE for any active assignment: an active
 * package whose services have no monthly entitlement configured (or simply
 * zero deliveries this month) renders a zero/empty state — it must never
 * silently disappear.
 */
import type {
  ClientPackageAssignmentProgress,
  ClientPackageServiceProgress,
} from '../../api/types';

/**
 * A service worth listing in the detailed breakdown: it has a monthly
 * target or some recorded activity. Services with no monthly entitlement
 * AND no consumption are omitted from the detail list (but do not, on
 * their own, hide the whole section — see `selectProgressView`).
 */
export function isTracked(s: ClientPackageServiceProgress): boolean {
  return s.entitlement > 0 || s.carried_in > 0 || s.consumed > 0;
}

export interface ProgressView {
  /**
   * Whether to render the section at all. True iff the client has an
   * active assignment with progress data (the EF returns one entry per
   * active assignment). The section stays visible even when no service is
   * tracked — that case renders a zero/empty state, not `null`.
   */
  readonly visible: boolean;
  /**
   * Assignments reduced to their tracked (non-zero) services. MAY be empty
   * while `visible` is true → the caller renders the zero/empty state.
   */
  readonly tracked: ReadonlyArray<ClientPackageAssignmentProgress>;
  /** Month label for the section header ('YYYY-MM'). */
  readonly month: string | undefined;
}

export function selectProgressView(
  progress: ReadonlyArray<ClientPackageAssignmentProgress>,
): ProgressView {
  const tracked = progress
    .map((a) => ({ ...a, services: a.services.filter(isTracked) }))
    .filter((a) => a.services.length > 0);
  return {
    visible: progress.length > 0,
    tracked,
    month: tracked[0]?.month ?? progress[0]?.month,
  };
}
